import { Link } from "react-router-dom";
import { SITE, SERVICES } from "@/lib/site";
import { Facebook, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";

export default function SiteFooter() {
    const year = new Date().getFullYear();
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-navy-700 text-white overflow-hidden"
        >
            <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
            <div className="absolute inset-0 noise-overlay pointer-events-none" />
            <div className="relative rcps-container pt-16 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <Link to="/" className="inline-flex items-center gap-3">
                            <img src={SITE.logos.white} alt="RCPS Terrassement" className="h-12 w-auto" />
                        </Link>
                        <p className="mt-6 text-white/70 text-sm leading-relaxed max-w-sm">
                            RCPS Terrassement Sàrl est une entreprise basée à Plan-les-Ouates,
                            spécialisée dans les travaux de terrassement, démolition, génie civil,
                            voirie, enrobés, aménagements extérieurs et enrochement à Genève et
                            dans toute la Suisse romande.
                        </p>
                        <div className="mt-6 flex items-center gap-2">
                            <a
                                href={SITE.facebook}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Facebook RCPS"
                                className="w-10 h-10 rounded border border-white/15 flex items-center justify-center hover:bg-white/10 transition"
                            >
                                <Facebook className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <p className="kicker text-white/70">Prestations</p>
                        <ul className="mt-4 space-y-2">
                            {SERVICES.map((s) => (
                                <li key={s.slug}>
                                    <Link
                                        to={`/prestations/${s.slug}`}
                                        className="text-white/85 hover:text-white text-sm flex items-center gap-2"
                                    >
                                        <span className="w-2 h-px bg-white/40" /> {s.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <p className="kicker text-white/70">Navigation</p>
                        <ul className="mt-4 space-y-2">
                            {[
                                { l: "Accueil", to: "/" },
                                { l: "Entreprise", to: "/entreprise" },
                                { l: "Réalisations", to: "/realisations" },
                                { l: "Contact", to: "/contact" },
                            ].map((n) => (
                                <li key={n.to}>
                                    <Link to={n.to} className="text-white/85 hover:text-white text-sm">
                                        {n.l}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <p className="kicker text-white/70">Contact</p>
                        <ul className="mt-4 space-y-3 text-sm text-white/85">
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 mt-0.5 text-white/70" />
                                <span>
                                    {SITE.address.street}
                                    <br />
                                    {SITE.address.zip} {SITE.address.city}
                                </span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone className="w-4 h-4 text-white/70" />
                                <a href={`tel:${SITE.phoneTel}`} className="hover:text-white">
                                    {SITE.phone}
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone className="w-4 h-4 text-white/70" />
                                <a href={`tel:${SITE.phone2Tel}`} className="hover:text-white">
                                    {SITE.phone2}
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail className="w-4 h-4 text-white/70" />
                                <a href={`mailto:${SITE.email}`} className="hover:text-white break-all">
                                    {SITE.email}
                                </a>
                            </li>
                        </ul>
                        <Link
                            to="/contact"
                            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white group"
                        >
                            Demander un devis <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                        </Link>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-white/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
                    <p>© {year} RCPS Terrassement Sàrl – Tous droits réservés.</p>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <a
                            href={SITE.legal.mentions}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid="footer-legal-mentions"
                            className="hover:text-white transition"
                        >
                            Mentions légales
                        </a>
                        <a
                            href={SITE.legal.privacy}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid="footer-legal-privacy"
                            className="hover:text-white transition"
                        >
                            Politique de confidentialité
                        </a>
                        <a
                            href={SITE.legal.cgv}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid="footer-legal-cgv"
                            className="hover:text-white transition"
                        >
                            Conditions générales de prestations
                        </a>
                        <button
                            type="button"
                            data-testid="footer-legal-cookies"
                            onClick={() => window.dispatchEvent(new Event("rcps-open-cookie-prefs"))}
                            className="hover:text-white transition"
                        >
                            Gestion des cookies
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
}
